/**
 * components/viewer/RevisionImpactPanel.jsx — Iter 32 Inspector fusion.
 *
 * When the focused structure has changes captured in the active Revision
 * Review's impact_items, we render a "Revision Impact" section inside the
 * Inspector with:
 *   - Per-field prev → current diff (rim, pipe, slope, …)
 *   - Three field-response buttons: Acknowledge / Needs Clarification /
 *     Field Conflict
 *
 * That's the operational heartbeat. No dashboards. No threads. Just
 * the field user looking at the change, knowing it's been routed to
 * them, and stamping their response on the permanent operational record.
 */
import React, { useState } from "react";
import { CheckCircle2, HelpCircle, AlertOctagon, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { enqueue as enqueueSync, drainNow as drainSync } from "@/lib/syncQueue";


const RESPONSE_OPTIONS = [
  {
    state: "acknowledged",
    label: "Acknowledge",
    short: "I understand",
    icon: CheckCircle2,
    color: "#10b981",
    soft: "rgba(16, 185, 129, 0.12)",
  },
  {
    state: "needs_clarification",
    label: "Needs Clarification",
    short: "Something isn't clear",
    icon: HelpCircle,
    color: "#f59e0b",
    soft: "rgba(245, 158, 11, 0.12)",
  },
  {
    state: "field_conflict",
    label: "Field Conflict",
    short: "This creates a construction issue",
    icon: AlertOctagon,
    color: "var(--st-escalated, #ef4444)",
    soft: "rgba(239, 68, 68, 0.12)",
  },
];


/**
 * Props:
 *   review       — active RevisionReview doc (with impact_items)
 *   structure    — the currently focused structure
 *   myAck        — the current user's FieldAcknowledgment (or null if N/A)
 *   onResolved(updatedAck) — fired after successful PATCH
 */
export default function RevisionImpactPanel({ review, structure, myAck, onResolved }) {
  // Find the matching impact item for the focused structure (by id first
  // for spatial precision, then by structure_label as a fallback so seeded
  // demo data without structure_ids still works).
  const item = (review?.impact_items || []).find(
    (it) => (it.structure_id && it.structure_id === structure?.id)
         || it.structure_label === structure?.structure_label,
  );
  if (!item || !item.fields?.length) return null;

  return (
    <div
      className="border-t border-[var(--fop-border)] px-4 py-3"
      data-testid="revision-impact-panel"
      data-review-id={review.id}
      data-structure-label={item.structure_label}
    >
      {/* Header — orientation only, never noisy */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">
          Revision Impact
        </div>
        <div className="font-mono text-[9px] uppercase tracking-wider text-[var(--fop-text-3)]">
          REV {review.prior_revision} → REV {review.current_revision}
        </div>
      </div>

      {/* Diff list — each row is one field. Mono numerics, prev→current. */}
      <ul className="space-y-2.5" data-testid="impact-diff-list">
        {item.fields.map((f) => (
          <li key={f.key} className="text-[11px]" data-testid={`impact-row-${f.key}`}>
            <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-0.5">
              {f.field}
            </div>
            <div className="flex items-center gap-2 font-mono text-[12px] text-[var(--fop-text)]">
              <span className="text-[var(--fop-text-3)] line-through decoration-[var(--fop-text-3)]/40">
                {fmtVal(f.prev, f.unit)}
              </span>
              <ArrowRight size={10} className="text-[var(--fop-text-3)]" />
              <span className="font-bold text-[var(--fop-primary)]">
                {fmtVal(f.current, f.unit)}
              </span>
            </div>
          </li>
        ))}
      </ul>

      {/* Field response — only present when the current user IS the field
          assignee (myAck is non-null). Reviewers/PMs see the diff but no
          response buttons. */}
      {myAck && (
        <ResponseControls
          ack={myAck}
          onResolved={onResolved}
        />
      )}
    </div>
  );
}


function ResponseControls({ ack, onResolved }) {
  const [busy, setBusy] = useState(false);
  const [drawer, setDrawer] = useState(null); // {state} when comment expected
  const [comment, setComment] = useState("");

  const submit = async (state, withComment) => {
    if (busy) return;
    setBusy(true);
    const body = { state, response_comment: withComment ? (comment.trim() || null) : null };
    try {
      const { data } = await api.patch(`/acks/${ack.id}`, body, { timeout: 8000 });
      setDrawer(null);
      setComment("");
      onResolved?.(data);
    } catch (e) {
      // Iter 36 — queue the ack mutation so the field user's response
      // is preserved through any dead-zone. UI optimistically updates
      // via onResolved with a synthetic ack doc; the queue will replace
      // it server-side on drain.
      try {
        await enqueueSync({
          op_type: "ack.update",
          endpoint: `/acks/${ack.id}`,
          method: "PATCH",
          payload: body,
          scope_key: `ack:${ack.id}`,
        });
        drainSync();
        const synthetic = { ...ack, ...body, responded_at: new Date().toISOString(), _pending_sync: true };
        setDrawer(null);
        setComment("");
        onResolved?.(synthetic);
      } catch {/* swallow */}
    } finally {
      setBusy(false);
    }
  };

  const resolved = ack.state !== "pending";

  return (
    <div className="mt-3 pt-3 border-t border-[var(--fop-border)]/60" data-testid="field-response">
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[var(--fop-text-3)] mb-1.5">
        Field Response
      </div>

      {/* Current state badge — when already resolved, surface that clearly
          (no "are you sure?" prompt; supers can re-resolve if they need to,
          per backend policy). */}
      {resolved && (
        <div
          className="mb-2 text-[11px] font-mono uppercase tracking-wider"
          style={{
            color: RESPONSE_OPTIONS.find((o) => o.state === ack.state)?.color || "var(--fop-text-2)",
          }}
          data-testid="field-response-current"
          data-state={ack.state}
        >
          You · {RESPONSE_OPTIONS.find((o) => o.state === ack.state)?.label || ack.state}
        </div>
      )}

      {drawer ? (
        <div className="space-y-2" data-testid="field-response-drawer" data-state={drawer.state}>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            placeholder={
              drawer.state === "field_conflict"
                ? "What's the construction issue? (helps the PM resolve.)"
                : "What needs clarification? (optional)"
            }
            className="w-full text-[11px] resize-none"
            data-testid="field-response-comment"
            autoFocus
          />
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => { setDrawer(null); setComment(""); }}
              className="btn btn-ghost text-[10px]"
              data-testid="field-response-cancel"
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => submit(drawer.state, true)}
              className="btn btn-primary text-[10px]"
              data-testid="field-response-submit"
              disabled={busy}
            >
              Submit
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-1.5">
          {RESPONSE_OPTIONS.map((o) => {
            const Icon = o.icon;
            const isCurrent = ack.state === o.state;
            return (
              <button
                key={o.state}
                type="button"
                onClick={() => {
                  // Acknowledge is one-tap; the other two prompt for a quick comment.
                  if (o.state === "acknowledged") submit(o.state, false);
                  else setDrawer({ state: o.state });
                }}
                disabled={busy || isCurrent}
                className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-[11px] font-mono transition-colors border text-left ${
                  isCurrent ? "opacity-60 cursor-default" : "hover:brightness-110"
                }`}
                style={{
                  color: o.color,
                  background: isCurrent ? o.soft : "transparent",
                  borderColor: o.color,
                }}
                data-testid={`field-response-${o.state}`}
                data-current={isCurrent ? "1" : "0"}
              >
                <Icon size={12} />
                <span className="font-bold">{o.label}</span>
                <span className="text-[var(--fop-text-3)] ml-auto truncate">{o.short}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


function fmtVal(v, unit) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") {
    return unit ? `${v} ${unit}` : `${v}`;
  }
  return unit ? `${v} ${unit}` : `${v}`;
}
